/**
 * Vollbild für die Minispiele zu Ereignissen.
 *
 * Das Rätsel kommt vom Server (`view`), gespielt wird im Browser, abgegeben
 * wird die Lösung – der Server rechnet sie nach. Die Uhr läuft am Server; hier
 * wird sie nur über den beim Abruf gemessenen Versatz angezeigt.
 */
import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { MINIGAME_LABEL, type CablingPuzzle, type EscapePuzzle, type EventMinigame, type GameEvent, type TdGame } from '@hosting-sim/core';
import { Button, Spinner } from '../ui';
import { useGame } from '../../store/game';
import { CablingGame } from './CablingGame';
import { TowerDefenseGame } from './TowerDefenseGame';
import { EscapeGame } from './EscapeGame';

export interface MinigameApi {
  /** Lösung abgeben; liefert das Serverergebnis. */
  submit: (payload: unknown) => Promise<{ ok: boolean; message: string; data?: unknown }>;
  /** Verbleibende Zeit in ms nach Serveruhr. */
  remainingMs: () => number;
  /** Lokaler Zeitabzug für Fehlgriffe (nur Anzeige). */
  penalize: (ms: number) => void;
  /** Spiel ist entschieden: Ergebnis zeigen. */
  finish: (won: boolean, note: string) => void;
}

/** Was vor dem Start zu lesen ist – die Uhr läuft erst mit „Los“. */
const INTRO: Record<EventMinigame['kind'], { title: string; lines: string[] }> = {
  cabling: {
    title: 'Netzersatzanlage anklemmen',
    lines: [
      'Links hängen die farbigen Adern der mobilen Netzersatzanlage, rechts die Klemmen im Schaltschrank.',
      'Die Klemmen tragen nur Farbkürzel (z. B. BR = braun, RT/WS = rot mit weißem Streifen). Der Aufkleber rechts übersetzt sie.',
      'Ader anklicken, dann die passende Klemme. Zuerst den grün-gelben Schutzleiter!',
      'Jeder Fehlgriff kostet ein paar Sekunden.',
    ],
  },
  'tower-defense': {
    title: 'Abwehrlinie halten',
    lines: [
      'Angriffspakete laufen vom Internet über die Leitung zu deinem Server.',
      'Stell Filter neben die Leitung. Jeder Filter erkennt nur seine Paketart: UDP-Filter → UDP, SYN-Proxy → SYN, Rate-Limit → HTTP. Die Scrubbing-Appliance erkennt alles, ist aber teuer.',
      'Rechts steht, was die nächste Welle bringt. Deck alle Arten ab, nicht nur die häufigste.',
      'Drei Wellen. Kommt höchstens ein Fünftel durch, gibt der Angreifer auf. Zwischen den Wellen kannst du nachkaufen.',
    ],
  },
  escape: {
    title: 'Aus der Zelle ausbrechen',
    lines: [
      'Der Angreifer hat deinen Zugang in eine eingeschränkte Shell umgebogen – du sitzt in einer Zelle.',
      'Die Tür hat vier Schlösser. Jedes öffnet sich mit einer Spur vom System: PID des Schadprozesses, Port der Hintertür, IP und Konto des Angreifers.',
      'Kein Linux nötig: Jedes Schloss erklärt, worauf du achten musst, und hat einen blauen Knopf, der den passenden Befehl ausführt.',
      'Lies die Ausgabe in der Shell links und wähl dann eine von vier Antworten. Jede falsche Wahl kostet 20 Sekunden.',
      'Wer sich auskennt, kann in der Shell auch selbst tippen.',
    ],
  },
};

interface ViewData {
  minigame: EventMinigame;
  puzzle: unknown;
  nowMs: number;
}

export function MinigameOverlay({ event, onClose }: { event: GameEvent; onClose: () => void }) {
  const run = useGame((s) => s.run);
  const refreshWorld = useGame((s) => s.refreshWorld);
  const [view, setView] = useState<ViewData | null>(null);
  const [offset, setOffset] = useState(0);
  const [penalty, setPenalty] = useState(0);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<{ won: boolean; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setNow] = useState(0);
  // Ein frisches Spiel beginnt mit der Erklärung; ein laufendes geht direkt weiter.
  const [begun, setBegun] = useState(event.minigame!.state !== 'ready');

  const kind = event.minigame!.kind;

  // Starten (falls noch nicht geschehen) und Rätsel holen.
  useEffect(() => {
    if (!begun) return;
    let cancelled = false;
    (async () => {
      if (event.minigame!.state === 'ready') {
        const started = await run({ type: 'event/minigame', eventId: event.id, action: 'start' }, { quiet: true });
        if (!started.ok) {
          if (!cancelled) setError(started.message);
          return;
        }
      }
      const result = await run({ type: 'event/minigame', eventId: event.id, action: 'view' }, { quiet: true });
      if (cancelled) return;
      if (!result.ok || !result.data) {
        setError(result.message || 'Das Minispiel ließ sich nicht laden.');
        return;
      }
      const data = result.data as ViewData;
      setOffset(data.nowMs - Date.now());
      setDeadline(data.minigame.deadlineMs);
      setView(data);
      if (data.minigame.state === 'won' || data.minigame.state === 'lost') {
        setOutcome({ won: data.minigame.state === 'won', note: data.minigame.resultNote ?? '' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [event.id, begun]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sekundentakt für die Uhr.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);

  const remainingMs = useCallback(
    () => (deadline === null ? 0 : deadline - (Date.now() + offset) - penalty),
    [deadline, offset, penalty],
  );

  const finish = useCallback(
    (won: boolean, note: string) => {
      setOutcome({ won, note });
      void refreshWorld();
    },
    [refreshWorld],
  );

  // Zeit um: aufgeben, damit das Ereignis sofort wieder läuft.
  const timeUp = view !== null && !outcome && deadline !== null && remainingMs() <= 0;
  useEffect(() => {
    if (!timeUp) return;
    void run({ type: 'event/minigame', eventId: event.id, action: 'abort' }, { quiet: true });
    finish(false, 'Die Zeit ist abgelaufen – das Ereignis läuft normal weiter.');
  }, [timeUp]); // eslint-disable-line react-hooks/exhaustive-deps

  const api: MinigameApi = {
    submit: async (payload) => {
      const result = await run({ type: 'event/minigame', eventId: event.id, action: 'submit', payload }, { quiet: true });
      const data = result.data as { deadlineMs?: number } | undefined;
      if (data?.deadlineMs) setDeadline(data.deadlineMs);
      return result;
    },
    remainingMs,
    penalize: (ms) => setPenalty((p) => p + ms),
    finish,
  };

  async function giveUp() {
    await run({ type: 'event/minigame', eventId: event.id, action: 'abort' }, { quiet: true });
    finish(false, 'Abgebrochen – das Ereignis läuft normal weiter.');
  }

  const seconds = Math.max(0, Math.ceil(remainingMs() / 1000));
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-950/85 p-2 backdrop-blur-sm sm:p-4">
      <div className="relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl">
        <header className="flex flex-wrap items-center gap-3 border-b border-zinc-700 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-2xs uppercase tracking-wider text-zinc-400">Selbst eingreifen</p>
            <h2 className="truncate text-base font-semibold">
              {MINIGAME_LABEL[kind]} <span className="font-normal text-zinc-400">· {event.title}</span>
            </h2>
          </div>
          {view && !outcome && (
            <span
              className={clsx(
                'rounded-md px-3 py-1 font-mono text-lg tabular-nums',
                seconds <= 15 ? 'animate-pulse bg-red-600 text-white' : 'bg-zinc-800 text-zinc-100',
              )}
              aria-label="Verbleibende Zeit"
            >
              {clock}
            </span>
          )}
          {!begun ? (
            <Button size="sm" variant="secondary" onClick={onClose}>
              Doch nicht
            </Button>
          ) : view && !outcome ? (
            <Button size="sm" variant="secondary" onClick={() => void giveUp()}>
              Aufgeben
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
          )}
        </header>

        <div className="relative min-h-[320px] flex-1 overflow-auto p-3 sm:p-4">
          {!begun && (
            <div className="mx-auto max-w-xl py-4">
              <h3 className="text-xl font-semibold">{INTRO[kind].title}</h3>
              <ul className="mt-4 space-y-2 text-sm text-zinc-300">
                {INTRO[kind].lines.map((line) => (
                  <li key={line} className="flex gap-2">
                    <span className="text-amber-400">›</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-zinc-400">
                Nur ein Versuch. Die Uhr startet erst mit „Los“; ab dann ist das Ereignis angehalten, bis du fertig bist.
              </p>
              <Button className="mt-5" variant="primary" onClick={() => setBegun(true)}>
                Los
              </Button>
            </div>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
          {begun && !view && !error && (
            <div className="flex h-64 items-center justify-center">
              <Spinner />
            </div>
          )}
          {view && kind === 'cabling' && <CablingGame puzzle={view.puzzle as CablingPuzzle} api={api} disabled={!!outcome} />}
          {view && kind === 'tower-defense' && <TowerDefenseGame game={view.puzzle as TdGame} api={api} disabled={!!outcome} />}
          {view && kind === 'escape' && (
            <EscapeGame puzzle={view.puzzle as EscapePuzzle} solved={view.minigame.solved} api={api} disabled={!!outcome} />
          )}

          {outcome && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/80 p-6">
              <div className="max-w-md rounded-xl border border-zinc-700 bg-zinc-900 p-6 text-center shadow-xl">
                <p className={clsx('text-3xl font-bold', outcome.won ? 'text-emerald-400' : 'text-amber-400')}>
                  {outcome.won ? 'Geschafft!' : 'Nicht ganz'}
                </p>
                <p className="mt-3 text-sm text-zinc-300">{outcome.note}</p>
                <Button className="mt-5" variant="primary" onClick={onClose}>
                  Zurück zum Ereignis
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
